import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import Animated, { LinearTransition } from 'react-native-reanimated'

import { FigmaChevronRightIcon } from '../icons/FigmaSystemIcons'
import { Box, Text, useTheme } from '../theme/restyle'
import { MOTION_DURATION, MOTION_EASING, nativeMotion } from '../utils/motion'

const AnimatedBox = Animated.createAnimatedComponent(Box)

// The foot grows to seat the button that overlaps it, so it has to grow with
// that button's entrance rather than pop out from under it.
const FOOT_TRANSITION =
  LinearTransition.duration(MOTION_DURATION).easing(MOTION_EASING)

// The card's own inset, and the foot it grows when the Continue button overlaps
// it — 40 of padding under 19 of button, leaving the design's 21pt gap.
const HORIZONTAL_PADDING = 12
const VERTICAL_PADDING = 13
const ATTACHED_BOTTOM_PADDING = 40

export interface WithdrawSourceCardProps {
  /** The 36px token-on-chain avatar. */
  icon: ReactNode
  /** The source as the host names it, e.g. `USDC (Perps)`. */
  title: string
  /** The spendable balance, already grouped and denominated. */
  subtitle?: string
  useMaxLabel: string
  /** Omitted once an amount is typed, or when no balance is known. */
  onUseMax?: () => void
  /** Omitted when the host offers a single source — nothing to pick. */
  onPress?: () => void
  accessibilityLabel: string
  /** Squares the foot of the card off for the button that overlaps it. */
  attachedToButton?: boolean
}

/**
 * What the withdrawal is drawn from, sitting above the keypad: the source
 * token, its balance, and the shortcut that spends all of it.
 */
export function WithdrawSourceCard({
  icon,
  title,
  subtitle,
  useMaxLabel,
  onUseMax,
  onPress,
  accessibilityLabel,
  attachedToButton = false,
}: WithdrawSourceCardProps) {
  const theme = useTheme()

  // The source picker is two hit areas rather than a card-wide one wrapping the
  // "Use max" button: nested touchables are legal on native, but a touchable
  // inside a touchable is a button inside a button in the DOM.
  return (
    <AnimatedBox
      flexDirection="row"
      alignItems="center"
      layout={nativeMotion(FOOT_TRANSITION)}
      style={[
        styles.card,
        {
          backgroundColor: theme.background.secondary,
          borderTopLeftRadius: theme.borderRadii.element,
          borderTopRightRadius: theme.borderRadii.element,
          borderBottomLeftRadius: attachedToButton
            ? 0
            : theme.borderRadii.element,
          borderBottomRightRadius: attachedToButton
            ? 0
            : theme.borderRadii.element,
          paddingBottom: attachedToButton
            ? ATTACHED_BOTTOM_PADDING
            : VERTICAL_PADDING,
        },
      ]}
    >
      <TokenPressable onPress={onPress} accessibilityLabel={accessibilityLabel}>
        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          style={styles.token}
        >
          {icon}
          <Box flex={1} style={styles.details}>
            <Text variant="bodyMedium" numberOfLines={1}>
              {title}
            </Text>
            {subtitle ? (
              <Text
                variant="detail"
                numberOfLines={1}
                style={{ color: theme.content.elements.secondary }}
              >
                {subtitle}
              </Text>
            ) : null}
          </Box>
        </Box>
      </TokenPressable>
      {onUseMax ? (
        <Pressable
          onPress={onUseMax}
          accessibilityRole="button"
          accessibilityLabel={useMaxLabel}
        >
          {/* Filled from the border token, as the design has it: no button
              surface carries a fill that reads against the card. */}
          <Box
            borderRadius="button"
            alignItems="center"
            justifyContent="center"
            style={[
              styles.useMax,
              {
                height: theme.sizes.buttonXs,
                backgroundColor: theme.border.elements.default.color,
              },
            ]}
          >
            <Text
              variant="bodyMedium"
              numberOfLines={1}
              style={{ color: theme.content.button.secondary.default }}
            >
              {useMaxLabel}
            </Text>
          </Box>
        </Pressable>
      ) : null}
      {onPress ? (
        // The chevron repeats the token area's target so the right edge stays
        // tappable; the labelled button beside it already carries the
        // semantics, so this one is decoration to a screen reader.
        <Pressable
          onPress={onPress}
          accessibilityElementsHidden
          focusable={false}
        >
          <FigmaChevronRightIcon
            size={16}
            color={theme.content.elements.secondary}
          />
        </Pressable>
      ) : null}
    </AnimatedBox>
  )
}

/** The source itself as a target, or plain content when there is nothing to pick. */
function TokenPressable({
  onPress,
  accessibilityLabel,
  children,
}: {
  onPress?: () => void
  accessibilityLabel: string
  children: ReactNode
}) {
  if (!onPress) {
    return <Box flex={1}>{children}</Box>
  }
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={styles.tokenTarget}
    >
      {children}
    </Pressable>
  )
}

const styles = StyleSheet.create({
  card: {
    gap: 8,
    paddingHorizontal: HORIZONTAL_PADDING,
    paddingTop: VERTICAL_PADDING,
  },
  tokenTarget: { flex: 1 },
  token: { gap: 10 },
  details: { gap: 2 },
  useMax: { paddingHorizontal: 16 },
})
